/**
 * Integrations OAuth round trip.
 *
 * The provider only carries an opaque `state` = base64url of
 * `{ nonce, mcp_id }`. The nonce and the form data needed after the return
 * stay in this tab's sessionStorage, so typed credentials never reach the
 * provider URL, and a return whose state doesn't match the flow this tab
 * started is refused.
 */

export const OAUTH_FLOW_STORAGE_KEY = 'tara.integrations.oauthFlow';

export interface OauthFlowState {
  nonce: string;
  mcp_id: string | number;
}

export interface StoredOauthFlow extends OauthFlowState {
  form: Record<string, unknown>;
}

const toBase64Url = (text: string): string =>
  // UTF-8-escape first: btoa() throws on anything outside Latin-1.
  btoa(unescape(encodeURIComponent(text)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const fromBase64Url = (value: string): string => {
  const b64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  return decodeURIComponent(escape(atob(padded)));
};

export const newNonce = (): string => {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

export const encodeOauthState = (state: OauthFlowState): string =>
  toBase64Url(JSON.stringify({ nonce: state.nonce, mcp_id: state.mcp_id }));

export const decodeOauthState = (state: string): OauthFlowState | null => {
  try {
    const parsed = JSON.parse(fromBase64Url(state));
    if (
      parsed &&
      typeof parsed.nonce === 'string' &&
      parsed.nonce &&
      (typeof parsed.mcp_id === 'string' || typeof parsed.mcp_id === 'number')
    ) {
      return { nonce: parsed.nonce, mcp_id: parsed.mcp_id };
    }
  } catch {
    // fall through
  }
  return null;
};

/**
 * Remembers a new flow for `mcpId` with the form data needed after the
 * return, and returns the state to send to the provider. Starting a flow
 * replaces any earlier one in this tab.
 */
export const startOauthFlow = (
  mcpId: string | number,
  form: Record<string, unknown>
): string => {
  const flow: StoredOauthFlow = { nonce: newNonce(), mcp_id: mcpId, form };
  sessionStorage.setItem(OAUTH_FLOW_STORAGE_KEY, JSON.stringify(flow));
  return encodeOauthState(flow);
};

const readStoredFlow = (): StoredOauthFlow | null => {
  try {
    const raw = sessionStorage.getItem(OAUTH_FLOW_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.nonce === 'string' && parsed.nonce) {
      return parsed as StoredOauthFlow;
    }
  } catch {
    // fall through
  }
  return null;
};

export const clearOauthFlow = (): void => {
  sessionStorage.removeItem(OAUTH_FLOW_STORAGE_KEY);
};

/**
 * Checks a returned `state` against the flow this tab started. On a match the
 * stored flow is removed (one use only) and returned; otherwise null, and the
 * stored flow is left alone.
 */
export const consumeOauthFlow = (state: string): StoredOauthFlow | null => {
  const returned = decodeOauthState(state);
  const stored = readStoredFlow();
  if (!returned || !stored) return null;
  if (
    returned.nonce !== stored.nonce ||
    String(returned.mcp_id) !== String(stored.mcp_id)
  ) {
    return null;
  }
  clearOauthFlow();
  return stored;
};
